import { useCallback, useEffect, useMemo, useState } from "react";
import { Map as MapIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CoverageMap, REP_COLORS, UNOWNED } from "@/components/leads/CoverageMap";
import { api, errMsg } from "@/lib/api";

const PickPanel = ({ area, reps, onClose, onSaved }) => {
  const [rep, setRep] = useState("");
  const [busy, setBusy] = useState(false);
  const owners = area.owners.map((o) => o.id);
  const save = async () => {
    const r = reps.find((x) => x.id === rep);
    setBusy(true);
    try { await api.put(`/leads/areas/${r.id}`, { areas: [...r.areas, area.area] }); toast.success(`${r.name} now owns ${area.area}`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[#E7E2D6] bg-white p-3 text-sm bf-rise" data-testid="coverage-pick-panel">
      <div className="min-w-[160px] flex-1"><div className="font-semibold" data-testid="coverage-pick-area">{area.area}</div><div className="text-xs text-muted-foreground">{area.leads} leads · {area.owners.length ? `Owner: ${area.owners.map((o) => o.name).join(", ")}` : <span className="font-semibold text-rose-600">No owner yet</span>}</div></div>
      {reps.length === 0 ? <span className="text-xs text-muted-foreground">Add salespeople in Team first</span> : <>
        <select value={rep} onChange={(e) => setRep(e.target.value)} className="h-9 rounded-full border border-[#E7E2D6] bg-white px-3 text-sm" data-testid="coverage-pick-rep">
          <option value="">Choose salesperson</option>
          {reps.filter((r) => !owners.includes(r.id)).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <Button size="sm" disabled={!rep || busy} onClick={save} className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="coverage-pick-save">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}{area.owners.length ? "Add owner" : "Make owner"}</Button>
      </>}
      <button onClick={onClose} className="rounded-full p-1.5 transition-colors hover:bg-[#F1EDE3]" aria-label="Close" data-testid="coverage-pick-close"><X className="h-4 w-4" /></button>
    </div>
  );
};

const Legend = ({ reps, colorOf, unowned }) => (
  <div className="flex flex-wrap gap-2 text-xs" data-testid="coverage-legend">
    {reps.map((r) => <span key={r.id} className="flex items-center gap-1.5 rounded-full border border-[#E7E2D6] bg-white px-2.5 py-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf(r.id) }} />{r.name}</span>)}
    <span className="flex items-center gap-1.5 rounded-full border border-dashed border-rose-300 bg-rose-50 px-2.5 py-1 font-semibold text-rose-700" data-testid="coverage-unowned-count"><span className="h-2.5 w-2.5 rounded-full" style={{ background: UNOWNED }} />No owner: {unowned} areas</span>
  </div>
);

export const AreaCoverage = ({ refresh }) => {
  const [data, setData] = useState(null);
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [picked, setPicked] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/leads/coverage").then((r) => setData(r.data)).catch(() => setData(null)); }, [refresh, tick]);
  const colorOf = useCallback((id) => REP_COLORS[Math.max(0, (data?.reps || []).findIndex((r) => r.id === id)) % REP_COLORS.length], [data]);
  const shown = useMemo(() => (data?.areas || []).filter((a) => !onlyGaps || a.owners.length === 0), [data, onlyGaps]);
  if (!data) return null;
  const gaps = data.areas.filter((a) => a.owners.length === 0);
  return (
    <div className="mb-6 space-y-3" data-testid="area-coverage">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-display text-lg font-semibold"><MapIcon className="h-4 w-4 text-[#D96B43]" />Area coverage</div>
          <div className="text-xs text-muted-foreground" data-testid="coverage-summary">{data.areas.length} areas with map pins · bigger circle = more leads{data.no_pin ? ` · ${data.no_pin} ${data.no_pin === 1 ? "lead has" : "leads have"} no map pin` : ""}</div>
        </div>
        <button onClick={() => setOnlyGaps((v) => !v)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${onlyGaps ? "border-rose-600 bg-rose-600 text-white" : "border-[#E7E2D6] bg-white hover:border-rose-400"}`} data-testid="coverage-only-gaps">Only areas with no owner</button>
      </div>
      <Legend reps={data.reps} colorOf={colorOf} unowned={gaps.length} />
      <CoverageMap areas={shown} colorOf={colorOf} kitchen={data.kitchen} onPick={setPicked} />
      {picked ? <PickPanel key={picked.area} area={picked} reps={data.reps} onClose={() => setPicked(null)} onSaved={() => { setPicked(null); setTick((t) => t + 1); }} />
        : <div className="text-xs text-muted-foreground" data-testid="coverage-click-hint">Click any circle to choose its owner.</div>}
      {gaps.length > 0 && <div className="text-xs text-muted-foreground" data-testid="coverage-gaps">Biggest gaps: {gaps.slice(0, 6).map((a) => `${a.area} (${a.leads})`).join(", ")}. Give them an owner in Area owners.</div>}
    </div>
  );
};
